import { useState, useEffect, useMemo } from 'react';
import { microSessionService } from '@/services/microSessionService';
import { MicroSession } from '@/data/microSessions';

/**
 * Hook for synchronous session lookup (file-based only)
 * Use this for catalog lookups that don't need DB prompt overlay
 */
export const useMicroSession = (microId: string): MicroSession | undefined => {
  return useMemo(() => microSessionService.getSessionById(microId), [microId]);
};

/**
 * Extended session type with optional chat intro from DB
 */
export type MicroSessionWithPrompts = MicroSession & { _chatIntro?: string };

/**
 * Hook for async session loading with database prompt overlay
 * Falls back to file-based prompts if no DB override exists
 */
export const useMicroSessionWithPrompts = (microId: string) => {
  const [session, setSession] = useState<MicroSessionWithPrompts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadSession = async () => {
      if (!microId) {
        setSession(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await microSessionService.getSessionWithPrompts(microId);
        if (!cancelled) {
          setSession(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to load session'));
          // Fall back to sync lookup on error
          const fallback = microSessionService.getSessionById(microId);
          setSession(fallback || null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadSession();

    return () => {
      cancelled = true;
    };
  }, [microId]);

  return { session, loading, error };
};
